<script lang="ts">
	//Helpers
	import { deleteDevice } from '../../../../helpers/add_delete_helpers';
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	import { capFirstChar } from '../../../../helpers/utilities';
	import { getFactoryResetSuccessToastTitle } from '../../../../helpers/device_settings_helpers';

	//Services
	import { format } from '../../../../services/i18n';
	import { postReq, localReq } from '../../../../services/http';

	//Constants
	import { NO_SWITCH_RESET, SLEEPING_TYPES } from '../../../../constants';

	//Configs
	import { devices_config } from '@devices/index';

	//Stores
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';

	//Design system components
	import { Alert, Button, toast, Toggle } from '@Shelly/shelly-design-system';

	//Components
	import { resetDrawerClose } from 'src/helpers/drawer_helpers';
	import FactoryResetModal from 'src/components/FactoryResetModal.svelte';

	export let device;

	const id = real_id(device.id);

	let localIp = get_local_ip(id);

	let showModal = false;

	let settings = readDeviceSettings(id);
	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore ?? settings;
	const type = devices_config[device.type].friendly_name;
	const hasPowerCycleReset = typeof settings?.pon_wifi_reset !== 'undefined';
	const hasResetFromInput = devices_config?.[device?.type]?.reset_from_input;
	const hasResetFromSwitch = NO_SWITCH_RESET.indexOf(device.type) === -1;
	const alertHeadingPowerCycle = $format('alert_reset_device_by_power_cycle', {
		default: 'By enabling this setting you can reset the device by quick 5 successive power cycles.',
	});
	const alertHeading = $format('alert_reset_device_from_input', {
		default: 'By enabling this setting you can reset the device from the device input.',
	});
	const toggleHeadingPowerCycle = $format('label_factory_reset_enable_by_power_cycle', {
		default: 'Enable factory reset by power cycle',
	});
	const toggleHeading = $format('label_factory_reset_enable_from_input', {
		default: 'Enable factory reset from input',
	});

	let checkedPowerCycle = settings?.pon_wifi_reset;
	let checked = settings?.factory_reset_from_switch;

	let loading = false;
	let factoryLoading = false;

	const handleToggler = () => {
		checked = !checked;
		saveExternal();
	};
	const handleTogglerPowerCycle = () => {
		checkedPowerCycle = !checkedPowerCycle;
		savePowerCycle();
	};

	const saveExternal = async () => {
		const stringState = checked
			? $format('reset_from_switch_enabled_scc_toast')
			: $format('reset_from_switch_disabled_scc_toast');
		loading = true;

		let res;

		if (localIp) {
			res = await localReq(device, `${localIp}/settings`, 'post', {
				factory_reset_from_switch: checked,
			});
		} else {
			res = await postReq(`/device/reset_from_switch`, {
				id: id,
				reset: checked ? 1 : 0,
			});
		}
		if (res.isok) {
			settings.factory_reset_from_switch = checked;

			toast.open({ type: 'success', title: stringState });
		} else {
			checked = !checked;
			toast.open({
				type: 'danger',
				title: $format('reset_from_switch_err_toast'),
			});
		}

		loading = false;
	};
	const savePowerCycle = async () => {
		const stringState = $format('setting_scc_saved_toast');
		loading = true;

		let res;

		if (localIp) {
			res = await localReq(device, `${localIp}/settings`, 'post', {
				pon_wifi_reset: true,
			});
		} else {
			res = await postReq('/device/settings/pon_wifi_reset', {
				id,
				enabled: checkedPowerCycle ? 1 : 0,
			});
		}
		if (res.isok) {
			if (hasPowerCycleReset) {
				settings['pon_wifi_reset'] = checkedPowerCycle;
			}
			toast.open({ type: 'success', title: stringState });
		} else {
			checkedPowerCycle = !checkedPowerCycle;
			toast.open({
				type: 'danger',
				title: $format('error_global'),
			});
		}

		loading = false;
	};
	const factoryReset = async () => {
		factoryLoading = true;
		let res;

		if (localIp) {
			res = await localReq(device, `${localIp}/settings`, 'post', {
				reset: 1,
			});
		} else {
			res = await postReq(`/device/factory_reset?id=${id}`, { id: id });
		}

		if (res.isok) {
			resetDrawerClose();
			toast.open({
				type: 'success',
				title: getFactoryResetSuccessToastTitle(device),
				duration: 10000,
			});
			await deleteDevice(id);
		} else if (res?.errors?.code == 500) {
			await deleteDevice(id);
			// toast.open({type:'danger', title:$format('factory_reset_err_toast')});
		} else {
			toast.open({ type: 'danger', title: $format('factory_reset_err_toast') });
		}
		factoryLoading = false;
	};

	const props = {
		title: `${$format('reset_device_to_factory_stngs', {
			default: 'Reset Device to Factory Settings',
		})}?`,
		name: device?.name,
		text: $format('resetting_device_msg', {
			values: { device_name: device.name },
			default:
				'Resetting the device ‘{device_name}’ to its factory settings will erase all data and settings, including its network configuration. To reconnect, use its Access Point network.',
		}),
		alert: {
			heading: $format('resseting_device_alert_msg', {
				default:
					'Resetting the device to factory settings will also delete the device from the app.',
			}),
			type: 'danger',
		},
		button_1: $format('button_reset_device', {
			default: 'Reset device',
		}),
		button_1_variant: 'danger',
		button_2: $format('button_cancel'),
		callback: factoryReset,
		isSleepingDevice: SLEEPING_TYPES.includes(device.type),
	};
</script>

{#if hasResetFromSwitch || hasResetFromInput}
	<Alert class="mb-s" type="info" heading={alertHeading} />

	<Toggle class="mb-m" size="m" {checked} on:click={handleToggler} id="toggle-4f2upsh">
		{toggleHeading}
	</Toggle>
{/if}
{#if hasPowerCycleReset && !SLEEPING_TYPES.includes(device.type)}
	<Alert class="mb-s" type="info" heading={alertHeadingPowerCycle} />

	<Toggle
		class="mb-m"
		size="m"
		checked={checkedPowerCycle}
		on:click={handleTogglerPowerCycle}
		id="toggle-92rcwrf"
	>
		{toggleHeadingPowerCycle}
	</Toggle>
{/if}

<Alert
	class="mb-s"
	type="warning"
	heading={$format('factory_reset_description', { values: { type } })}
/>

<Button loading={factoryLoading} size="m" width="full" on:click={() => (showModal = true)}
	>{capFirstChar($format('button_factory_reset'))}</Button
>
{#if showModal}
	<FactoryResetModal bind:showModal {props} />
{/if}
